/**
 * LEGACY / DEPRECATED (Pic2Content Core 1.0, Phase 0.5 — Generation 1).
 * Reachable only by direct URL (/editor/:itemId/:assetId) — not linked
 * from AppLayout's nav. Do not build new product features on this page;
 * the current editors are ShadowEditorModal/BackgroundEditorModal/
 * TemplateCutoutEditorModal (AssetChain). See
 * docs/architecture/PIC2CONTENT_CORE_1_0.md.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Collapse,
  InputNumber,
  Select,
  Slider,
  Space,
  Switch,
  Tag,
  Tooltip,
  message,
} from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  copyVersion,
  deleteVersion,
  exportAsset,
  getAsset,
  getItem,
  listEngines,
  markFinal,
  removeBackground,
  renderVersion,
  restoreAutoMask,
  saveMask,
  saveParams,
  suggestAdjust,
} from '../api/client'
import MaskCanvas, { exportMaskBlob, type BrushMode, type ViewMode } from '../components/editor/MaskCanvas'
import type { Asset, EngineInfo, Item, ProcessingVersion } from '../types'
import { statusColor } from '../utils/status'

const defaultColor = {
  brightness: 0,
  contrast: 0,
  temperature: 0,
  tint: 0,
  highlights: 0,
  shadows: 0,
  saturation: 0,
  auto_wb: false,
}

const defaultGeo = {
  rotation_90: 0,
  rotation_fine: 0,
  flip_h: false,
  flip_v: false,
  crop: null as null | Record<string, number>,
}

const defaultWhite = {
  background_color: '#FFFFFF',
  canvas_ratio: 'original',
  subject_scale: 0.9,
  auto_center: true,
  safe_margin: 0.05,
  forbid_upscale: true,
}

const defaultShadow = {
  mode: 'none',
  opacity: 0.45,
  blur: 18,
  offset_x: 0,
  offset_y: 0.08,
  scale_x: 1,
  scale_y: 0.35,
  perspective: 0.25,
  contact_strength: 0.7,
  color: '#000000',
}

const defaultMaskPost = {
  remove_small_noise_area: 64,
  fill_holes_area: 256,
  preserve_real_holes: true,
  edge_smooth: 0.5,
  feather: 0,
  dilate: 0,
  erode: 0,
  decontaminate: true,
}

export default function EditorPage() {
  const { itemId, assetId } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState<Item | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [asset, setAsset] = useState<Asset | null>(null)
  const [versions, setVersions] = useState<ProcessingVersion[]>([])
  const [version, setVersion] = useState<ProcessingVersion | null>(null)
  const [engines, setEngines] = useState<EngineInfo[]>([])
  const [engineName, setEngineName] = useState<string | undefined>()
  const [viewMode, setViewMode] = useState<ViewMode>('cutout')
  const [bgMode, setBgMode] = useState<'checker' | 'white' | 'black' | 'gray'>('checker')
  const [brushMode, setBrushMode] = useState<BrushMode>('paint')
  const [brushSize, setBrushSize] = useState(24)
  const [brushHardness, setBrushHardness] = useState(0.7)
  const [brushOpacity, setBrushOpacity] = useState(1)
  const [compareRatio, setCompareRatio] = useState(0.5)
  const [dirty, setDirty] = useState(false)
  const [color, setColor] = useState(defaultColor)
  const [geo, setGeo] = useState(defaultGeo)
  const [white, setWhite] = useState(defaultWhite)
  const [shadow, setShadow] = useState(defaultShadow)
  const [maskPost, setMaskPost] = useState(defaultMaskPost)
  const maskRef = useRef<HTMLCanvasElement | null>(null)

  const load = async () => {
    if (!itemId || !assetId) return
    const [{ data: itemData }, { data: assetData }, { data: eng }] = await Promise.all([
      getItem(itemId),
      getAsset(assetId),
      listEngines(),
    ])
    setItem(itemData.item)
    setAssets(itemData.assets)
    setAsset(assetData.asset)
    setVersions(assetData.versions)
    const latest = assetData.versions[assetData.versions.length - 1] || null
    setVersion(latest)
    setEngines(eng)
    setEngineName(eng.find((e) => e.is_default)?.name)
    if (latest) {
      setColor({ ...defaultColor, ...(latest.color_parameters as object) })
      setGeo({ ...defaultGeo, ...(latest.geometry_parameters as object) })
      setWhite({ ...defaultWhite, ...(latest.white_bg_parameters as object) })
      setShadow({ ...defaultShadow, ...(latest.shadow_parameters as object) })
      setMaskPost({ ...defaultMaskPost, ...(latest.mask_post_parameters as object) })
    }
  }

  useEffect(() => {
    load().catch((e) => message.error(e.message))
    const t = setInterval(() => {
      if (!assetId) return
      getAsset(assetId)
        .then(({ data }) => {
          setAsset(data.asset)
          setVersions(data.versions)
          if (!dirty) {
            const latest = data.versions[data.versions.length - 1] || null
            if (latest && latest.id !== version?.id) setVersion(latest)
          }
        })
        .catch(() => undefined)
    }, 4000)
    return () => clearInterval(t)
  }, [itemId, assetId])

  const idx = useMemo(() => assets.findIndex((a) => a.id === assetId), [assets, assetId])

  const goNeighbor = (delta: number) => {
    const next = assets[idx + delta]
    if (next && itemId) navigate(`/editor/${itemId}/${next.id}`)
  }

  const onSaveDraft = async (asNew = false) => {
    if (!assetId || !maskRef.current) return
    if (dirty) {
      const blob = await exportMaskBlob(maskRef.current)
      const { data } = await saveMask(assetId, blob, { as_new_version: asNew, version_id: version?.id })
      setVersion(data)
      setDirty(false)
    }
    const { data: v } = await saveParams(assetId, {
      version_id: version?.id,
      as_new_version: false,
      geometry: geo,
      color,
      shadow,
      white_bg: white,
      mask_post: maskPost,
      re_render: true,
    })
    setVersion(v)
    message.success(asNew ? '已另存为新版本' : '草稿已保存')
    await load()
  }

  const onMarkFinal = async () => {
    if (!version) return
    if (dirty) await onSaveDraft(false)
    const { data } = await markFinal(version.id, true)
    setVersion(data)
    message.success('已标记为最终版本')
    await load()
  }

  const proxyUrl = asset?.proxy_url || ''
  const maskUrl = version?.mask_url || undefined

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Space>
          <Link to={`/assets/${itemId}`}>← 返回物品</Link>
          <strong>
            {item?.item_code} / {asset?.original_filename}
          </strong>
          {asset && <Tag color={statusColor[asset.status]}>{asset.status_label}</Tag>}
          {version?.needs_manual_review && <Tag color="orange">建议人工复核</Tag>}
        </Space>
        <Space>
          <Button disabled={idx <= 0} onClick={() => goNeighbor(-1)}>
            上一张
          </Button>
          <Button disabled={idx < 0 || idx >= assets.length - 1} onClick={() => goNeighbor(1)}>
            下一张
          </Button>
        </Space>
      </div>

      <div className="editor-page">
        <div className="editor-side">
          <div className="thumb-list">
            {assets.map((a) => (
              <div
                key={a.id}
                className={`thumb-item ${a.id === assetId ? 'active' : ''}`}
                onClick={() => navigate(`/editor/${itemId}/${a.id}`)}
              >
                {a.thumbnail_url && <img src={a.thumbnail_url} alt="" />}
                <div style={{ fontSize: 11, marginTop: 4 }}>{a.status_label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="editor-canvas-wrap">
          <div className="editor-toolbar">
            <Select
              size="small"
              value={viewMode}
              style={{ width: 120 }}
              onChange={setViewMode}
              options={[
                { value: 'original', label: '原图' },
                { value: 'mask', label: '蒙版' },
                { value: 'cutout', label: '透明结果' },
                { value: 'white', label: '白底' },
                { value: 'black', label: '黑底' },
                { value: 'gray', label: '灰底' },
                { value: 'shadow', label: '阴影结果' },
                { value: 'compare', label: '对比' },
              ]}
            />
            <Select
              size="small"
              value={bgMode}
              style={{ width: 110 }}
              onChange={setBgMode}
              options={[
                { value: 'checker', label: '棋盘格' },
                { value: 'white', label: '白色' },
                { value: 'black', label: '黑色' },
                { value: 'gray', label: '中灰' },
              ]}
            />
            <Button size="small" type={brushMode === 'paint' ? 'primary' : 'default'} onClick={() => setBrushMode('paint')}>
              保留画笔
            </Button>
            <Button size="small" type={brushMode === 'erase' ? 'primary' : 'default'} onClick={() => setBrushMode('erase')}>
              橡皮
            </Button>
            <Button size="small" type={brushMode === 'pan' ? 'primary' : 'default'} onClick={() => setBrushMode('pan')}>
              拖动画布
            </Button>
            <Button size="small" onClick={() => window.dispatchEvent(new Event('editor-undo'))}>
              撤销
            </Button>
            <Button size="small" onClick={() => window.dispatchEvent(new Event('editor-redo'))}>
              重做
            </Button>
            {viewMode === 'compare' && (
              <Slider style={{ width: 120 }} min={0} max={1} step={0.01} value={compareRatio} onChange={setCompareRatio} />
            )}
            {dirty && <Tag color="gold">蒙版未保存</Tag>}
          </div>
          {proxyUrl ? (
            <MaskCanvas
              imageUrl={proxyUrl}
              maskUrl={maskUrl}
              overlayUrl={version?.shadow_image_url}
              viewMode={viewMode}
              brushMode={brushMode}
              brushSize={brushSize}
              brushHardness={brushHardness}
              brushOpacity={brushOpacity}
              compareRatio={compareRatio}
              bgMode={bgMode}
              onMaskChange={setDirty}
              maskRef={maskRef}
            />
          ) : (
            <div style={{ color: '#aaa', padding: 40 }}>加载中…</div>
          )}
        </div>

        <div className="editor-right">
          <Collapse
            defaultActiveKey={['auto', 'mask', 'geo', 'color', 'white', 'shadow', 'save', 'versions']}
            items={[
              {
                key: 'auto',
                label: '自动抠图',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      value={engineName}
                      onChange={setEngineName}
                      options={engines.map((e) => ({
                        value: e.name,
                        label: `${e.description || e.name}${e.loading ? '（加载中）' : ''}`,
                      }))}
                    />
                    <Button
                      type="primary"
                      block
                      onClick={async () => {
                        await removeBackground(assetId!, engineName)
                        message.info('抠图任务已排队，模型首次加载可能需要等待')
                      }}
                    >
                      执行自动抠图
                    </Button>
                    <Button
                      block
                      disabled={!version}
                      onClick={async () => {
                        if (!version) return
                        await restoreAutoMask(version.id)
                        message.success('已恢复自动初始蒙版')
                        setDirty(false)
                        await load()
                      }}
                    >
                      恢复自动初始蒙版
                    </Button>
                  </Space>
                ),
              },
              {
                key: 'mask',
                label: '蒙版修正',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>画笔大小</div>
                    <Slider min={2} max={120} value={brushSize} onChange={setBrushSize} />
                    <div>硬度</div>
                    <Slider min={0} max={1} step={0.05} value={brushHardness} onChange={setBrushHardness} />
                    <div>透明度</div>
                    <Slider min={0.1} max={1} step={0.05} value={brushOpacity} onChange={setBrushOpacity} />
                    <div>边缘平滑</div>
                    <Slider
                      min={0}
                      max={3}
                      step={0.1}
                      value={maskPost.edge_smooth}
                      onChange={(v) => setMaskPost({ ...maskPost, edge_smooth: v })}
                    />
                    <div>羽化</div>
                    <Slider
                      min={0}
                      max={8}
                      step={0.1}
                      value={maskPost.feather}
                      onChange={(v) => setMaskPost({ ...maskPost, feather: v })}
                    />
                    <div>膨胀 / 收缩</div>
                    <Space>
                      <InputNumber
                        value={maskPost.dilate}
                        min={0}
                        max={20}
                        onChange={(v) => setMaskPost({ ...maskPost, dilate: Number(v || 0) })}
                      />
                      <InputNumber
                        value={maskPost.erode}
                        min={0}
                        max={20}
                        onChange={(v) => setMaskPost({ ...maskPost, erode: Number(v || 0) })}
                      />
                    </Space>
                    <div>填补小孔 / 去噪点</div>
                    <Space>
                      <InputNumber
                        value={maskPost.fill_holes_area}
                        onChange={(v) => setMaskPost({ ...maskPost, fill_holes_area: Number(v || 0) })}
                      />
                      <InputNumber
                        value={maskPost.remove_small_noise_area}
                        onChange={(v) => setMaskPost({ ...maskPost, remove_small_noise_area: Number(v || 0) })}
                      />
                    </Space>
                    <div>
                      去边缘杂色{' '}
                      <Switch
                        checked={maskPost.decontaminate}
                        onChange={(v) => setMaskPost({ ...maskPost, decontaminate: v })}
                      />
                    </div>
                    <Tooltip title="框选主体重新计算：接口已预留，第一版请用画笔修正">
                      <Button block disabled>
                        框选主体（预留）
                      </Button>
                    </Tooltip>
                  </Space>
                ),
              },
              {
                key: 'geo',
                label: '几何调整',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Button onClick={() => setGeo({ ...geo, rotation_90: (geo.rotation_90 + 3) % 4 })}>
                        左旋90°
                      </Button>
                      <Button onClick={() => setGeo({ ...geo, rotation_90: (geo.rotation_90 + 1) % 4 })}>
                        右旋90°
                      </Button>
                    </Space>
                    <div>任意角度微调</div>
                    <Slider
                      min={-15}
                      max={15}
                      step={0.1}
                      value={geo.rotation_fine}
                      onChange={(v) => setGeo({ ...geo, rotation_fine: v })}
                    />
                    <Space>
                      <Button onClick={() => setGeo({ ...geo, flip_h: !geo.flip_h })}>水平翻转</Button>
                      <Button onClick={() => setGeo({ ...geo, flip_v: !geo.flip_v })}>垂直翻转</Button>
                    </Space>
                    <Button
                      onClick={async () => {
                        const { data } = await suggestAdjust(assetId!)
                        message.info(`建议水平校正角度：${data.auto_level.toFixed(2)}°（未自动应用）`)
                      }}
                    >
                      自动水平校正建议
                    </Button>
                    <Button
                      onClick={() => {
                        setGeo(defaultGeo)
                      }}
                    >
                      恢复原始几何
                    </Button>
                  </Space>
                ),
              },
              {
                key: 'color',
                label: '颜色调整',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {(
                      [
                        ['brightness', '亮度'],
                        ['contrast', '对比度'],
                        ['temperature', '色温'],
                        ['tint', '色调'],
                        ['highlights', '高光'],
                        ['shadows', '阴影'],
                        ['saturation', '饱和度'],
                      ] as const
                    ).map(([key, label]) => (
                      <div key={key}>
                        <div>{label}</div>
                        <Slider
                          min={-1}
                          max={1}
                          step={0.01}
                          value={color[key] as number}
                          onChange={(v) => setColor({ ...color, [key]: v })}
                        />
                      </div>
                    ))}
                    <Button
                      onClick={async () => {
                        const { data } = await suggestAdjust(assetId!)
                        setColor({
                          ...color,
                          temperature: data.auto_wb.temperature || 0,
                          tint: data.auto_wb.tint || 0,
                        })
                        message.success('已应用白平衡建议（可继续微调）')
                      }}
                    >
                      白平衡自动建议
                    </Button>
                    <Button onClick={() => setColor(defaultColor)}>重置颜色</Button>
                  </Space>
                ),
              },
              {
                key: 'white',
                label: '白底设置',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      value={white.canvas_ratio}
                      onChange={(v) => setWhite({ ...white, canvas_ratio: v })}
                      options={[
                        { value: 'original', label: '保持原图尺寸' },
                        { value: '1:1', label: '1:1 方形' },
                        { value: '3:4', label: '3:4' },
                        { value: '4:5', label: '4:5' },
                        { value: 'custom', label: '自定义像素' },
                      ]}
                    />
                    <div>主体占比</div>
                    <Slider
                      min={0.5}
                      max={1}
                      step={0.01}
                      value={white.subject_scale}
                      onChange={(v) => setWhite({ ...white, subject_scale: v })}
                    />
                    <div>
                      自动居中{' '}
                      <Switch checked={white.auto_center} onChange={(v) => setWhite({ ...white, auto_center: v })} />
                    </div>
                    <div>
                      禁止放大{' '}
                      <Switch
                        checked={white.forbid_upscale}
                        onChange={(v) => setWhite({ ...white, forbid_upscale: v })}
                      />
                    </div>
                  </Space>
                ),
              },
              {
                key: 'shadow',
                label: '阴影设置',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      value={shadow.mode}
                      onChange={(v) => setShadow({ ...shadow, mode: v })}
                      options={[
                        { value: 'none', label: '无阴影' },
                        { value: 'contact', label: '接触阴影' },
                        { value: 'soft_drop', label: '柔和落地阴影' },
                        { value: 'floating', label: '悬浮阴影' },
                      ]}
                    />
                    <div>透明度</div>
                    <Slider min={0} max={1} step={0.01} value={shadow.opacity} onChange={(v) => setShadow({ ...shadow, opacity: v })} />
                    <div>模糊</div>
                    <Slider min={0} max={60} value={shadow.blur} onChange={(v) => setShadow({ ...shadow, blur: v })} />
                    <div>水平/垂直偏移</div>
                    <Slider min={-0.3} max={0.3} step={0.01} value={shadow.offset_x} onChange={(v) => setShadow({ ...shadow, offset_x: v })} />
                    <Slider min={-0.1} max={0.4} step={0.01} value={shadow.offset_y} onChange={(v) => setShadow({ ...shadow, offset_y: v })} />
                    <div>透视压缩 / 接触强度</div>
                    <Slider min={0} max={0.8} step={0.01} value={shadow.perspective} onChange={(v) => setShadow({ ...shadow, perspective: v })} />
                    <Slider min={0} max={1} step={0.01} value={shadow.contact_strength} onChange={(v) => setShadow({ ...shadow, contact_strength: v })} />
                    <Button onClick={() => setShadow(defaultShadow)}>重置阴影</Button>
                  </Space>
                ),
              },
              {
                key: 'save',
                label: '保存和确认',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button type="primary" block onClick={() => onSaveDraft(false)}>
                      保存草稿
                    </Button>
                    <Button block onClick={() => onSaveDraft(true)}>
                      另存为新版本
                    </Button>
                    <Button block type="primary" ghost onClick={onMarkFinal}>
                      标记为最终版本
                    </Button>
                    <Button
                      block
                      disabled={!version?.is_final}
                      onClick={async () => {
                        if (!version) return
                        await markFinal(version.id, false)
                        message.success('已取消最终版本')
                        await load()
                      }}
                    >
                      取消最终版本
                    </Button>
                    <Button
                      block
                      onClick={async () => {
                        if (dirty) await onSaveDraft(false)
                        else if (version) await renderVersion(version.id)
                        const { data } = await exportAsset(assetId!, { make_zip: true })
                        message.success(`导出完成：${data.base_name}`)
                      }}
                    >
                      导出当前图片
                    </Button>
                    <Button block onClick={() => goNeighbor(1)}>
                      进入下一张
                    </Button>
                  </Space>
                ),
              },
              {
                key: 'versions',
                label: '版本历史',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {versions.map((v) => (
                      <div
                        key={v.id}
                        style={{
                          border: v.id === version?.id ? '1px solid #1f6f5b' : '1px solid #eee',
                          padding: 8,
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setVersion(v)
                          setDirty(false)
                          setColor({ ...defaultColor, ...(v.color_parameters as object) })
                          setGeo({ ...defaultGeo, ...(v.geometry_parameters as object) })
                          setWhite({ ...defaultWhite, ...(v.white_bg_parameters as object) })
                          setShadow({ ...defaultShadow, ...(v.shadow_parameters as object) })
                        }}
                      >
                        <div>
                          v{v.version_number} {v.is_final ? '· 最终' : v.is_draft ? '· 草稿' : ''}
                          {v.is_manually_edited ? ' · 人工' : ' · 自动'}
                        </div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {new Date(v.created_at).toLocaleString()}
                        </div>
                        <Space size={4} style={{ marginTop: 4 }}>
                          <Button
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation()
                              await copyVersion(v.id)
                              await load()
                            }}
                          >
                            复制
                          </Button>
                          {!v.is_final && (
                            <Button
                              size="small"
                              danger
                              onClick={async (e) => {
                                e.stopPropagation()
                                await deleteVersion(v.id)
                                await load()
                              }}
                            >
                              删除
                            </Button>
                          )}
                        </Space>
                      </div>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}
